import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, categories } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import { faqList, aboutInfo } from '@/mocks/about';
import { useState } from 'react';

const featuredVideos = videos.filter((v) => v.featured);
const latestVideos = videos.slice(0, 8);

const memberBenefits = [
  { icon: 'ri-4k-line', title: '4K超清画质', desc: '畅享极致清晰的视觉体验，每一帧都纤毫毕现' },
  { icon: 'ri-surround-sound-line', title: '杜比全景声', desc: '沉浸式三维音效，仿佛置身电影场景之中' },
  { icon: 'ri-smartphone-line', title: '多终端同步', desc: '手机、平板、电视无缝切换，观看进度实时同步' },
  { icon: 'ri-download-line', title: '离线下载', desc: '视频下载到本地，随时随地离线观看' },
  { icon: 'ri-vip-crown-line', title: '会员专属', desc: '独家内容抢先看，尊享会员专属福利活动' },
  { icon: 'ri-shield-check-line', title: '正版保障', desc: '全部内容正版授权，品质有保障观看更安心' },
];

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const pageTitle = '爱液视频官网 - 官方正版视频平台';
  const pageDescription = '爱液视频官网是您值得信赖的官方视频平台，提供海量正版高清视频内容，极速播放体验，多终端同步观看。立即访问爱液视频官网，畅享优质视频娱乐生活，注册即享专属会员福利。';
  const pageKeywords = '爱液视频官网,爱液视频官网入口,爱液视频官方网站,爱液视频在线观看,爱液视频高清播放,爱液视频会员注册,爱液视频最新地址';

  return (
    <>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl="https://www.aiyevideo.com"
        schemaData={[{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqList.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }]}
      />

      <header>
        <Navbar />
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20warm%20atmosphere%20with%20soft%20golden%20and%20amber%20gradient%20lighting%2C%20abstract%20geometric%20shapes%2C%20clean%20minimalist%20composition%2C%20luxurious%20digital%20entertainment%20background%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20smooth%20texture&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
            alt="爱液视频 - 正版高清视频平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              正版高清，极速畅享
            </h1>
            <p className="text-base md:text-lg text-background-50/85 mb-8 max-w-xl mx-auto">
              海量正版高清视频内容，极速播放体验，多终端同步观看。立即加入爱液视频，开启您的专属观影之旅。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/remen"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-play-fill mr-1.5"></i>立即观看
              </Link>
              <Link
                to="/about"
                className="bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 bg-background-50">
          <Breadcrumb items={[{ label: '首页', href: '/' }]} />
        </div>

        {/* Featured Videos */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">全网最受欢迎的视频内容，每日更新精选推荐</p>
              </div>
              <Link
                to="/category/remen"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-product-shop="">
              {featuredVideos.slice(0, 2).map((video) => (
                <VideoCard key={video.id} video={video} featured />
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5" data-product-shop="">
              {featuredVideos.slice(2, 6).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* Member Benefits */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                会员专属特权
              </h2>
              <p className="text-sm text-foreground-500 mt-2">开通会员，畅享极致观影体验</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {memberBenefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${benefit.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">{benefit.title}</h3>
                  <p className="text-xs text-foreground-500">{benefit.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/remen"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                <i className="ri-vip-crown-line mr-1.5"></i>立即开通会员
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Videos */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <i className="ri-play-circle-line text-accent-500 mr-2"></i>
                  最新上线
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最新正版高清视频，精彩内容不容错过</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop="">
              {latestVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-accent-500 py-12 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.brand.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-background-50">{stat.value}</div>
                  <div className="text-sm text-background-50/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <i className="ri-article-line text-secondary-600 mr-2"></i>
                  最新资讯
                </h2>
                <p className="text-sm text-foreground-500 mt-1">观影指南、精彩推荐，尽在爱液视频</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 3).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-2">为您解答关于爱液视频的常见疑问</p>
            </div>
            <div className="space-y-3">
              {faqList.map((faq, index) => (
                <div
                  key={index}
                  className="border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-50 transition-colors"
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  >
                    <span className="font-medium text-foreground-900 text-sm pr-4">{faq.question}</span>
                    <i className={`${activeFaq === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 transition-transform flex-shrink-0`}></i>
                  </button>
                  {activeFaq === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20amber%20and%20teal%20waves%2C%20smooth%20flowing%20curves%2C%20minimalist%20digital%20art%20style%2C%20clean%20elegant%20composition%2C%20cinematic%20texture&width=1800&height=600&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              准备好开启您的观影之旅了吗？
            </h2>
            <p className="text-background-50/85 mb-8 text-sm md:text-base">
              注册即享7天免费会员体验，海量正版高清视频等您来看
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/remen"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                免费注册
              </Link>
              <Link
                to="/about"
                className="bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                了解详情
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}